import type { CsvTable } from './csv';
import { rowValues } from './csv';
import type { ImportIssue, ImportPlan, PlannedRow } from './types';
import { countActions, error, warning } from './types';

export type CustomerKind = 'company' | 'person';

/** Die Spalten des C.O.S.-Arista-Kundenexports (gemessen an Kundenadresse.CSV, 2026-07-17). */
export const aristaCustomerColumns = [
  'SUCHNAME', 'K_NUMMER', 'NACHNAME', 'PLZ', 'ORT', 'VORNAME', 'STRASSE', 'LAND',
  'KONTAKT', 'BRF_ANREDE', 'TEL', 'NACHNAME2', 'NACHNAME3', 'ANREDE', 'MOBIL', 'EMAIL', 'EMAIL2',
] as const;

/** Ohne diese Spalten ist es kein Kundenexport. */
const requiredColumns = ['K_NUMMER', 'NACHNAME'] as const;

/**
 * Spalten, die FORQ NICHT strukturiert übernimmt, sondern wörtlich in die Notizen schreibt.
 *
 * Warum nicht sauber zuordnen? Weil die Daten es nicht hergeben. Gemessen am echten Export:
 * - VORNAME ist mal eine Namensfortsetzung ("Schüler Gabelstapler Vertriebs-u" + "Service GmbH"),
 *   mal ein Ansprechpartner ("Herr Lars Werdezki"), mal ein echter Vorname ("Rosbach" + "Otto").
 * - NACHNAME2 ebenso: "Thomas Rex" (Person) vs "(ehemals Sauerlandvolt)" (Zusatz) vs
 *   "Postbus 18 7050 AA" (Adresse).
 * - KONTAKT ist mal "Sehr geehrte Damen u" (Anredefloskel), mal "Frau Hörenbaum" (Person).
 *
 * Keine Regel trennt das zuverlässig. Wer VORNAME auf "Vorname" zieht, legt einen Kunden
 * "Service GmbH Schüler Gabelstapler Vertriebs-u" an. Wörtlich in die Notiz heißt: nichts geht
 * verloren, nichts wird erfunden -- und ein Mensch kann es später dort sortieren, wo FORQ echte
 * Felder dafür hat.
 */
const notesColumns = ['VORNAME', 'NACHNAME2', 'NACHNAME3', 'KONTAKT', 'BRF_ANREDE', 'MOBIL', 'EMAIL2', 'SUCHNAME'] as const;

export interface CustomerAddressDraft {
  readonly street: string | null;
  readonly postalCode: string | null;
  readonly city: string | null;
  readonly countryCode: string;
}

export interface CustomerDraft {
  /** Die Arista-Nummer. Wird ÜBERNOMMEN, nicht neu vergeben -- eure Debitorenkonten hängen daran. */
  readonly number: string;
  readonly kind: CustomerKind;
  readonly companyName: string | null;
  readonly firstName: string | null;
  readonly lastName: string | null;
  readonly email: string | null;
  readonly phone: string | null;
  readonly notes: string | null;
  readonly billingAddress: CustomerAddressDraft | null;
}

export interface ExistingCustomer {
  readonly number: string;
  readonly displayName: string;
}

export interface CustomerImportOptions {
  /**
   * Was die ANREDE-Codes bedeuten -- kommt vom Anwender, nicht aus einer Vermutung.
   * Im echten Export: 8 (905x), 3 (251x), 0 (29x), 1 (3x), 7 (2x), 5 (1x), 9 (1x).
   * 8 und 3 sehen beide nach Firmen aus, 1 und 9 nach Personen -- ableiten lässt sich das nicht,
   * und die Anrede steht später auf echten Briefen.
   */
  readonly salutationKinds?: Readonly<Record<string, CustomerKind>>;
  /** Bereits vorhandene Kunden, für create-vs-update. */
  readonly existing?: readonly ExistingCustomer[];
}

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** Arista schreibt "-" statt leer. Das ist kein Straßenname. */
function clean(value: string): string | null {
  const trimmed = value.trim();
  if (trimmed === '' || trimmed === '-') return null;
  return trimmed;
}

function buildNotes(values: Readonly<Record<string, string>>): string | null {
  const lines: string[] = [];
  for (const column of notesColumns) {
    const value = clean(values[column] ?? '');
    if (value) lines.push(`${column}: ${value}`);
  }
  if (lines.length === 0) return null;
  return ['Aus C.O.S. Arista übernommen:', ...lines].join('\n');
}

/**
 * Ein Kunde nur dann eine "Person", wenn der Anwender den ANREDE-Code als solche erklärt hat.
 * Ohne Zuordnung: 'company' -- der Anzeigename stimmt dann trotzdem (er kommt aus NACHNAME),
 * also blockieren wir deswegen keine 1192 Kunden. Es gibt eine Warnung, keine Erfindung.
 */
function resolveKind(
  code: string,
  options: CustomerImportOptions,
  issues: ImportIssue[],
): CustomerKind {
  const mapped = options.salutationKinds?.[code.trim()];
  if (mapped) return mapped;
  if (clean(code)) {
    issues.push(warning('ANREDE', `Anrede-Code "${code.trim()}" ist nicht zugeordnet – wird als Firma angelegt.`));
  }
  return 'company';
}

export function planCustomerImport(
  table: CsvTable,
  options: CustomerImportOptions = {},
): ImportPlan<CustomerDraft> {
  const existingByNumber = new Map((options.existing ?? []).map((entry) => [entry.number, entry]));
  const missingColumns = requiredColumns.filter((column) => !table.header.includes(column));
  const unusedColumns = table.header.filter(
    (column) => !(aristaCustomerColumns as readonly string[]).includes(column),
  );

  // Innerhalb der DATEI doppelte Nummern: im echten Export genau eine (10488 zweimal identisch).
  // Die zweite Zeile würde die erste überschreiben -- das ist kein Import, das ist ein Zufall.
  const seenNumbers = new Map<string, number>();
  const rows: PlannedRow<CustomerDraft>[] = [];

  for (const row of table.rows) {
    const values = rowValues(table.header, row);
    const issues: ImportIssue[] = [];

    const number = clean(values.K_NUMMER ?? '') ?? '';
    const name = clean(values.NACHNAME ?? '');

    if (!number) issues.push(error('K_NUMMER', 'Ohne Kundennummer kann die Zeile nicht zugeordnet werden.'));
    else if (!/^\d+$/.test(number)) issues.push(error('K_NUMMER', `"${number}" ist keine Kundennummer.`));
    if (!name) issues.push(error('NACHNAME', 'Ohne Namen kann kein Kunde angelegt werden.'));

    const firstSeen = seenNumbers.get(number);
    if (number && firstSeen !== undefined) {
      issues.push(error('K_NUMMER', `Kundennummer ${number} steht schon in Zeile ${firstSeen}.`));
    } else if (number) {
      seenNumbers.set(number, row.line);
    }

    const kind = resolveKind(values.ANREDE ?? '', options, issues);

    const email = clean(values.EMAIL ?? '');
    if (email && !emailPattern.test(email)) {
      issues.push(warning('EMAIL', `"${email}" sieht nicht wie eine E-Mail-Adresse aus – wird nicht übernommen.`));
    }

    const country = (clean(values.LAND ?? '') ?? 'DE').toUpperCase();
    if (country.length !== 2) {
      // Im echten Export: "EGY" neben "EG". Beides Ägypten, eines davon falsch geschrieben.
      issues.push(warning('LAND', `Länderkennzeichen "${country}" ist nicht zweistellig – bitte prüfen.`));
    }

    const city = clean(values.ORT ?? '');
    const street = clean(values.STRASSE ?? '');
    const postalCode = clean(values.PLZ ?? '');
    if (!city) issues.push(warning('ORT', 'Kein Ort – der Kunde wird ohne Anschrift angelegt.'));

    const blocked = issues.some((issue) => issue.level === 'error');
    const draft: CustomerDraft | null = blocked ? null : {
      number,
      kind,
      companyName: kind === 'company' ? name : null,
      firstName: kind === 'person' ? clean(values.VORNAME ?? '') : null,
      lastName: kind === 'person' ? name : null,
      email: email && emailPattern.test(email) ? email : null,
      phone: clean(values.TEL ?? ''),
      notes: buildNotes(values),
      billingAddress: city ? { street, postalCode, city, countryCode: country } : null,
    };

    const existing = existingByNumber.get(number);
    rows.push({
      line: row.line,
      number,
      label: name ?? `Zeile ${row.line}`,
      action: blocked ? 'blocked' : existing ? 'update' : 'create',
      draft,
      issues,
    });
  }

  return {
    rows,
    counts: countActions(rows),
    blankRowsSkipped: table.blankRowsSkipped,
    unusedColumns,
    missingColumns,
  };
}

/**
 * Die ANREDE-Codes der Datei mit Häufigkeit und Beispielen -- damit der Anwender sie EINMAL
 * zuordnen kann, statt dass wir raten. Beispiele sind echte Namen aus der Datei: daran erkennt
 * man in zwei Sekunden, ob ein Code Firma oder Person meint.
 */
export interface SalutationCode {
  readonly code: string;
  readonly count: number;
  readonly samples: readonly string[];
}

export function collectSalutationCodes(table: CsvTable, sampleSize = 3): readonly SalutationCode[] {
  const grouped = new Map<string, { count: number; samples: string[] }>();
  for (const row of table.rows) {
    const values = rowValues(table.header, row);
    const code = (values.ANREDE ?? '').trim();
    if (!code) continue;
    const entry = grouped.get(code) ?? { count: 0, samples: [] };
    entry.count += 1;
    const name = clean(values.NACHNAME ?? '');
    if (name && entry.samples.length < sampleSize) entry.samples.push(name);
    grouped.set(code, entry);
  }
  return [...grouped.entries()]
    .map(([code, entry]) => ({ code, count: entry.count, samples: entry.samples }))
    .sort((left, right) => right.count - left.count);
}
